import ChangeCommentModal from '@/components/change-comment-modal';
import type { IBalance } from '@/entities/balance.entity';
import { money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { BanknoteIcon, BellIcon, CircleDollarSignIcon } from 'lucide-react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

interface ClientBalanceCardProps {
	balance: IBalance;
	onCopy: (text: string) => void;
	className?: string;
}

export default function ClientBalanceCard({ balance, onCopy, className }: ClientBalanceCardProps) {
	const hasBalance = balance.cardBalance != null;

	return (
		<div className={cn('rounded-lg border border-border bg-card p-2.5 shadow-sm border-l-[3px] border-l-chart-3', className)}>
			<div className='flex items-start gap-2'>
				<div className='flex size-7 shrink-0 items-center justify-center rounded-lg bg-chart-3/15 text-chart-3'>
					<CircleDollarSignIcon size={14} />
				</div>

				<div className='min-w-0 flex-1'>
					<div className='flex items-center justify-between gap-2'>
						<div className='flex items-center gap-0.5 min-w-0'>
							<button
								type='button'
								className='text-sm font-semibold text-chart-2 hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'
								onClick={() => onCopy(balance.sender)}
							>
								{balance.sender}
							</button>
							{balance.source === 'push' ? (
								<span className='inline-flex shrink-0 items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground'>
									<BellIcon size={10} />
									Push
								</span>
							) : null}
							<ChangeCommentModal currentComment={balance.comment} balance={balance} />
						</div>
						<span className='text-xs text-muted-foreground shrink-0'>
							<TimeAgo date={balance.createdAt} formatter={formatter} />
						</span>
					</div>

					{balance.comment ? (
						<button
							type='button'
							className='mt-1 rounded-md bg-[#e89bff]/10 px-2 py-0.5 text-xs text-[#e89bff] hover:bg-[#e89bff]/20 cursor-pointer select-none'
							onClick={() => onCopy(balance.comment || '')}
						>
							{balance.comment}
						</button>
					) : null}
				</div>
			</div>

			{hasBalance || balance.cardNumber ? (
				<div
					className={cn(
						'mt-1.5 flex items-center gap-2 w-full',
						hasBalance && balance.cardNumber ? 'justify-between' : '',
					)}
				>
					{balance.cardNumber ? (
						<button
							type='button'
							className='flex min-w-0 items-center gap-1 text-sm font-semibold text-chart-3 hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none'
							onClick={() => onCopy(balance.cardNumber)}
						>
							<BanknoteIcon size={14} className='opacity-60 shrink-0' />
							<span className='truncate'>{balance.cardNumber}</span>
						</button>
					) : null}
					{hasBalance ? (
						<button
							type='button'
							className='text-base font-bold text-destructive shrink-0 hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none'
							onClick={() => onCopy(String(balance.cardBalance))}
						>
							{money.format(balance.cardBalance)}
						</button>
					) : null}
				</div>
			) : null}

			{balance.text ? (
				<button
					type='button'
					className='mt-1.5 block w-full text-left rounded-lg bg-muted/40 p-2 text-sm text-muted-foreground hover:bg-muted/60 cursor-pointer select-none'
					onClick={() => onCopy(balance.text)}
				>
					{balance.text}
				</button>
			) : null}
		</div>
	);
}
